import { useMutation } from 'react-query';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { postV2ApiCompanyIdTravelTripsTripIdBookingsProperty } from 'src/client/spendesk-api';

import { type Room } from '../pages/PropertyDetailsPage/types';

export type BookerInformation = {
  email: string;
  name: string;
  phone: string;
};

type BookPropertyPayload = {
  propertyOfferId: string;
  tripId: string;
  bookerInformation: BookerInformation;
  room: Room;
  selectedRateId: string;
};

export const useBookProperty = () => {
  const companyId = useCompanyId();
  return useMutation(
    ['bookProperty', companyId],
    async ({
      propertyOfferId,
      tripId,
      bookerInformation,
      room,
      selectedRateId,
    }: BookPropertyPayload) => {
      const selectedRate = room.rateOptions.find(
        (rate) => rate.id === selectedRateId,
      );

      if (!selectedRate) {
        throw new Error('Selected rate not found');
      }

      const response =
        await postV2ApiCompanyIdTravelTripsTripIdBookingsProperty({
          path: {
            companyId,
            tripId,
          },
          body: {
            propertyOfferId,
            bookerInformation,
            room: {
              name: room.name,
              photos: room.photos,
              beds: room.beds,
              selectedRate: {
                id: selectedRate.id,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error dueAtAccommodation is not defined in the type, update after api is updated
                dueAtAccommodation: {},
                supplierRateId: selectedRate.supplierRateId,
                totalPrice: selectedRate.totalPrice,
                basePrice: selectedRate.basePrice,
                tax: selectedRate.tax,
                fees: selectedRate.fees,
                mealInformation: {
                  boardType: selectedRate.mealInformation.boardType,
                },
                refundInformation: {
                  type: selectedRate.refundInformation.type,
                  cancellationTimeline:
                    selectedRate.refundInformation.cancellationTimeline.map(
                      (timeline) => ({
                        before: timeline.deadline,
                        refundAmount: timeline.amount,
                      }),
                    ),
                },
              },
            },
          },
        });

      if (response.statusCode !== 200) {
        throw new Error('Failed to book property');
      }

      return response.body.data;
    },
    {
      retry: false,
    },
  );
};
